import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Sparkles, Key, Copy, Trash2, Plus } from "lucide-react";
import Layout from "@/components/Layout";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function ApiKeysCard() {
  const [keys, setKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const res = await api.get("/keys"); setKeys(res.data.keys || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/keys");
      setNewKey(res.data.api_key);
      await load();
      toast.success("API key created — copy it now, it won't be shown again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create key");
    } finally { setLoading(false); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/keys/${id}`); setKeys((k) => k.filter((x) => x.key_id !== id)); toast.success("Key revoked"); }
    catch { toast.error("Failed to revoke"); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="mt-10 max-w-2xl mx-auto text-left bg-[#121212] border border-white/10 rounded-xl p-6" data-testid="api-keys-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Key size={16} className="text-[#4da3ff]" />
          <h3 className="font-heading font-semibold text-white">Programmatic API Access</h3>
        </div>
        <button data-testid="generate-key-btn" onClick={generate} disabled={loading}
          className="btn-brand disabled:opacity-50 text-white rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors flex items-center gap-1.5">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} New key
        </button>
      </div>

      {newKey && (
        <div className="mt-4 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-lg p-3" data-testid="new-key-reveal">
          <p className="text-[#4da3ff] text-xs mb-1.5">Copy now — shown only once:</p>
          <div className="flex items-center gap-2">
            <code className="text-white text-xs break-all flex-1">{newKey}</code>
            <button onClick={() => copy(newKey)} className="text-zinc-300 hover:text-white shrink-0"><Copy size={15} /></button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {keys.length === 0 && <p className="text-zinc-600 text-sm">No API keys yet.</p>}
        {keys.map((k) => (
          <div key={k.key_id} className="flex items-center justify-between gap-3 border border-white/5 rounded-lg px-3 py-2">
            <div className="min-w-0">
              <code className="text-zinc-300 text-xs">{k.prefix}…</code>
              <p className="text-zinc-600 text-[11px] mt-0.5">created {new Date(k.created_at).toLocaleDateString()}{k.last_used ? ` · last used ${new Date(k.last_used).toLocaleDateString()}` : " · never used"}</p>
            </div>
            <button data-testid={`revoke-key-${k.key_id}`} onClick={() => revoke(k.key_id)} className="text-zinc-500 hover:text-[#FF3B30] shrink-0"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>

      <div className="mt-4 bg-black border border-white/10 rounded-lg p-3">
        <p className="text-zinc-500 text-[11px] mb-1.5">Pull a domain's handoff package from another app:</p>
        <code className="text-[#32D74B] text-xs break-all">curl -H "X-API-Key: YOUR_KEY" {API}/export/example.com</code>
      </div>
    </div>
  );
}

export default function Subscription() {
  const navigate = useNavigate();
  const { sub, plan, refreshSub } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | pending | failed | none
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      refreshSub();
      setStatus("none");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshSub();
          setStatus("success");
          window.history.replaceState(null, "", "/subscription");
          return;
        }
        if (res.data.status === "expired") { setStatus("failed"); return; }
      } catch {}
      if (attempts >= 6) { setStatus("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refreshSub]);

  if (status === "checking") {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-32 gap-3">
          <Loader2 size={30} className="animate-spin text-[#007AFF]" />
          <p className="text-zinc-400 font-mono text-sm">Confirming your payment…</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-lg mx-auto text-center py-20" data-testid="subscription-result">
        {status === "success" && (
          <>
            <CheckCircle2 size={48} className="text-[#32D74B] mx-auto" />
            <h1 className="font-heading text-3xl font-bold text-white mt-4">You're on {plan === "agency" ? "Agency" : "Pro"} 🎉</h1>
            <p className="text-zinc-400 mt-2">AI Build Briefs, exports and monitoring are now unlocked.</p>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 size={40} className="text-[#FFD60A] mx-auto animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mt-4">Payment processing…</h1>
            <p className="text-zinc-400 mt-2">This can take a moment. Refresh shortly to see your plan.</p>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={48} className="text-[#FF3B30] mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-white mt-4">Payment not completed</h1>
            <p className="text-zinc-400 mt-2">No charge was made. You can try again anytime.</p>
          </>
        )}
        {status === "none" && (
          <>
            <Sparkles size={40} className="text-[#4da3ff] mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-white mt-4">Your plan: {plan}</h1>
            {sub && <p className="text-zinc-400 mt-2 font-mono text-sm">{sub.domainsUsed} domain(s) tracked{sub.expires_at ? ` · renews ${new Date(sub.expires_at).toLocaleDateString()}` : ""}</p>}
          </>
        )}
        <div className="flex items-center justify-center gap-3 mt-8">
          <button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")} className="btn-brand text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">Go to dashboard</button>
          {plan === "free" && <button onClick={() => navigate("/pricing")} className="border border-white/20 text-white hover:bg-white/10 rounded-lg px-5 py-2.5 text-sm transition-colors">View plans</button>}
        </div>
        {plan === "agency" && <ApiKeysCard />}
      </div>
    </Layout>
  );
}
